import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { authApi } from '../../../lib/api';
import posthog from '../../../lib/posthog';

/**
 * Auth context for global auth state
 */
const AuthContext = createContext(null);

/**
 * useAuth hook - manages authentication state functionally
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/**
 * AuthProvider - provides auth state to app
 */
export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    loading: true,
    error: null
  });

  // Check current auth status on mount
  useEffect(() => {
    console.log('AuthProvider mounting, checking auth...');
    checkAuth();
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      console.log('checkAuth called');
      // Check both localStorage and sessionStorage for token
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const isRemembered = !!localStorage.getItem('authToken');

      if (token) {
        // Verify token with server
        try {
          const { authenticated, user } = await authApi.getStatus();
          console.log('Server auth check:', authenticated);

          if (authenticated && user) {
            // Store user back in the same storage that had the token
            if (isRemembered) {
              localStorage.setItem('user', JSON.stringify(user));
            } else {
              sessionStorage.setItem('user', JSON.stringify(user));
            }
            setState({ user, loading: false, error: null });
          } else {
            // Token invalid, clear both storages
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('user');
            setState({ user: null, loading: false, error: null });
          }
        } catch (error) {
          // Server check failed, clear auth from both storages
          console.error('Server auth check failed:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('user');
          setState({ user: null, loading: false, error: null });
        }
      } else {
        console.log('No auth token found');
        setState({ user: null, loading: false, error: null });
      }
    } catch (error) {
      console.error('checkAuth error:', error);
      setState({ user: null, loading: false, error: null });
    }
  }, []);

  const login = useCallback(async (email, password, rememberMe = false) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const { token, user } = await authApi.login(email, password, rememberMe);

      // Store token based on remember me preference
      if (rememberMe) {
        // Remember me: use localStorage (persists across sessions)
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        // Clear sessionStorage if it exists
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
      } else {
        // No remember me: use sessionStorage (cleared when browser closes)
        sessionStorage.setItem('authToken', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        // Clear localStorage if it exists
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }

      // Track login in PostHog
      posthog.identify(user.id.toString(), {
        email: user.email,
        displayName: user.display_name,
        subscriptionStatus: user.subscription_status || 'free',
      });

      posthog.capture('user_logged_in', {
        method: 'email',
        rememberMe: rememberMe
      });

      setState({ user, loading: false, error: null });
      return { success: true };
    } catch (error) {
      // Track login error
      posthog.capture('auth_error', {
        error: error.message,
        endpoint: '/api/auth/login',
        action: 'login'
      });

      setState(prev => ({ ...prev, loading: false, error: error.message }));
      return { success: false, error: error.message };
    }
  }, []);

  const signup = useCallback(async (email, password, name) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const { token, user } = await authApi.register(email, password, name);
      // On signup, default to remembering the user (use localStorage)
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      // Clear sessionStorage just in case
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');

      // Track signup in PostHog
      posthog.identify(user.id.toString(), {
        email: user.email,
        displayName: user.display_name || name,
        subscriptionStatus: user.subscription_status || 'free',
        signupDate: new Date().toISOString(),
        accountType: 'email'
      });

      posthog.capture('user_signed_up', {
        method: 'email',
        source: 'signup_page'
      });

      setState({ user, loading: false, error: null });
      return { success: true };
    } catch (error) {
      // Track signup error
      posthog.capture('auth_error', {
        error: error.message,
        endpoint: '/api/auth/signup',
        action: 'signup'
      });

      setState(prev => ({ ...prev, loading: false, error: error.message }));
      return { success: false, error: error.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Check BOTH storages to see if user was remembered
      // IMPORTANT: Check BEFORE clearing anything
      const localToken = localStorage.getItem('authToken');
      const sessionToken = sessionStorage.getItem('authToken');
      const localUser = localStorage.getItem('user');
      const sessionUser = sessionStorage.getItem('user');

      console.log('[useAuth] Logout called');
      console.log('[useAuth] localStorage.authToken:', !!localToken);
      console.log('[useAuth] sessionStorage.authToken:', !!sessionToken);
      console.log('[useAuth] localStorage.user:', !!localUser);
      console.log('[useAuth] sessionStorage.user:', !!sessionUser);

      // User was remembered if they had token AND user in localStorage
      const wasRemembered = !!(localToken && localUser);
      console.log('[useAuth] wasRemembered:', wasRemembered);

      // Track logout in PostHog
      posthog.capture('user_logged_out');

      // For remembered users: Keep token + user for auto-login
      // For session users: Clear everything
      if (wasRemembered) {
        console.log('[useAuth] ✅ User WAS remembered, keeping token + user for auto-login');
        // Keep localStorage.authToken and localStorage.user for Facebook-style auto-login
        // Just clear session
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
      } else {
        console.log('[useAuth] ❌ User was NOT remembered, clearing all auth data');
        // Clear everything from both storages
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');

        // Reset PostHog user identification (only if not remembered)
        posthog.reset();
      }

      setState({ user: null, loading: false, error: null });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const value = {
    ...state,
    login,
    signup,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}